import type { InteractionRecord } from '@/shared/analytics/AnalyticsTypes';

/**
 * Shared session-drift math. Drift is measured against a session's *dominant*
 * topic — the topic with the largest total-token footprint, treated as the
 * session's real purpose. Every turn spent on another topic is "drift": tokens
 * burned re-establishing context after wandering off. These helpers roll that
 * per-turn notion up to the session, topic, and person levels.
 */

export function topicOf(record: InteractionRecord): string {
    return record.topicLabel ?? '—';
}

function tokensOf(record: InteractionRecord): number {
    return record.inputTokens + record.outputTokens;
}

function costOf(record: InteractionRecord): number {
    return record.allocatedCostUsd;
}

/** Distinct topics a set of records touched — its drift breadth. */
export function driftTopicCount(records: InteractionRecord[]): number {
    return new Set(records.map(topicOf)).size;
}

function tokensByTopic(records: InteractionRecord[]): Map<string, number> {
    const byTopic = new Map<string, number>();
    for (const record of records) {
        byTopic.set(topicOf(record), (byTopic.get(topicOf(record)) ?? 0) + tokensOf(record));
    }
    return byTopic;
}

/** The topic carrying the largest token footprint across the given records. */
export function dominantTopic(records: InteractionRecord[]): string {
    let dominant = '';
    let max = -1;
    for (const [topic, tokens] of tokensByTopic(records)) {
        if (tokens > max) {
            max = tokens;
            dominant = topic;
        }
    }
    return dominant;
}

/**
 * The tokens burned wandering off a session's dominant topic — every turn on
 * another topic counts as excess. Operates on a single session's records.
 */
export function computeDriftExcessTokens(records: InteractionRecord[]): number {
    const dominant = dominantTopic(records);
    let excess = 0;
    for (const record of records) {
        if (topicOf(record) !== dominant) excess += tokensOf(record);
    }
    return excess;
}

function groupBy<T>(items: T[], key: (item: T) => string): Map<string, T[]> {
    const map = new Map<string, T[]>();
    for (const item of items) {
        const bucket = map.get(key(item));
        if (bucket === undefined) map.set(key(item), [item]);
        else bucket.push(item);
    }
    return map;
}

/**
 * Drift tokens attributed to each user, summed across their drifted sessions.
 * The input is the turn-level records of the drifted sessions only; each
 * session's excess is computed against its own dominant topic, then rolled up
 * per user. The returned map's keys are the set of drifting users.
 */
export function driftTokensByUser(driftRecords: InteractionRecord[]): Map<string, number> {
    const result = new Map<string, number>();
    for (const [userId, userRecords] of groupBy(driftRecords, (r) => r.userId)) {
        let total = 0;
        for (const sessionRecords of groupBy(userRecords, (r) => r.sessionId).values()) {
            total += computeDriftExcessTokens(sessionRecords);
        }
        result.set(userId, total);
    }
    return result;
}

/** Allocated $ attributed to each user's drifted turns — same per-session
 * dominant-topic attribution as {@link driftTokensByUser}, priced instead of
 * counted. Zero when cost data doesn't cover the turns. */
export function driftCostByUser(driftRecords: InteractionRecord[]): Map<string, number> {
    const result = new Map<string, number>();
    for (const [userId, userRecords] of groupBy(driftRecords, (r) => r.userId)) {
        let total = 0;
        for (const sessionRecords of groupBy(userRecords, (r) => r.sessionId).values()) {
            const dominant = dominantTopic(sessionRecords);
            for (const record of sessionRecords) {
                if (topicOf(record) !== dominant) total += costOf(record);
            }
        }
        result.set(userId, total);
    }
    return result;
}

/** Per-person drift row for the overview grid. All totals span the user's whole activity; only `driftTokens`/`driftCostUsd` come from their drifted sessions. */
export interface DriftUserRow {
    userId: string;
    totalTopics: number;
    totalSessions: number;
    topicsPerSession: number;
    totalTokens: number;
    driftTokens: number;
    /** Allocated $ burned on drifted turns; 0 when no cost data covers them. */
    driftCostUsd: number;
    /** Share of tokens spent on-topic — `(totalTokens − driftTokens) / totalTokens`, in `0..1`. */
    efficiency: number;
}

export interface DriftUserTotals {
    totalTokens: number;
    totalSessions: number;
}

/** Share of tokens spent on-topic — `(total − drift) / total`, in `0..1`; a driftless or empty set is fully efficient. */
export function onTopicEfficiency(totalTokens: number, driftTokens: number): number {
    return totalTokens > 0 ? (totalTokens - driftTokens) / totalTokens : 1;
}

/**
 * Joins per-user all-activity aggregates with drift tokens, restricted to the
 * drifting user set (the keys of `driftTokensByUser`). Users absent from the
 * aggregates fall back to zeroed totals.
 */
export function buildDriftUserRows(input: {
    driftTokensByUser: Map<string, number>;
    driftCostByUser: Map<string, number>;
    userTotals: Map<string, DriftUserTotals>;
    topicCountByUser: Map<string, number>;
}): DriftUserRow[] {
    const rows: DriftUserRow[] = [];
    for (const [userId, driftTokens] of input.driftTokensByUser) {
        const totals = input.userTotals.get(userId);
        const totalTokens = totals?.totalTokens ?? 0;
        const totalSessions = totals?.totalSessions ?? 0;
        const totalTopics = input.topicCountByUser.get(userId) ?? 0;
        rows.push({
            userId,
            totalTopics,
            totalSessions,
            topicsPerSession: totalSessions > 0 ? totalTopics / totalSessions : 0,
            totalTokens,
            driftTokens,
            driftCostUsd: input.driftCostByUser.get(userId) ?? 0,
            efficiency: onTopicEfficiency(totalTokens, driftTokens),
        });
    }
    return rows;
}

/**
 * A single turn flagged for drift against its session's dominant topic — the
 * leaf row of the person drill-in grid (grouped by session, then topic). The
 * `isDrift` flag is per-turn and additive, so token/drift roll-ups sum cleanly
 * at every grouping level without re-deriving each session's dominant topic.
 */
export interface DriftTurn {
    id: string;
    sessionId: string;
    topic: string;
    timestamp: string;
    tokens: number;
    /** This turn's allocated real spend ($); 0 when no cost data covers it. */
    costUsd: number;
    isDrift: boolean;
    /** Total drift tokens of this turn's whole session — lets the grid order session groups by drift. */
    sessionDriftTokens: number;
    /** The underlying turn, carried so a leaf-row click can open the interaction drawer. */
    record: InteractionRecord;
}

export function buildDriftTurns(records: InteractionRecord[]): DriftTurn[] {
    const turns: DriftTurn[] = [];
    for (const [sessionId, sessionRecords] of groupBy(records, (r) => r.sessionId)) {
        const dominant = dominantTopic(sessionRecords);
        const sessionDriftTokens = sessionRecords.reduce((sum, r) => sum + (topicOf(r) !== dominant ? tokensOf(r) : 0), 0);
        for (const record of sessionRecords) {
            const topic = topicOf(record);
            turns.push({
                id: record.id,
                sessionId,
                topic,
                timestamp: record.timestamp,
                tokens: tokensOf(record),
                costUsd: costOf(record),
                isDrift: topic !== dominant,
                sessionDriftTokens,
                record,
            });
        }
    }
    return turns;
}

/** Total tokens across a set of drift turns. */
export function sumTokens(turns: DriftTurn[]): number {
    return turns.reduce((sum, turn) => sum + turn.tokens, 0);
}

/** Tokens spent on drifted turns — those off their session's dominant topic. */
export function sumDriftTokens(turns: DriftTurn[]): number {
    return turns.reduce((sum, turn) => sum + (turn.isDrift ? turn.tokens : 0), 0);
}

/** Total allocated $ across a set of drift turns. */
export function sumCost(turns: DriftTurn[]): number {
    return turns.reduce((sum, turn) => sum + turn.costUsd, 0);
}

/** Allocated $ spent on drifted turns — those off their session's dominant topic. */
export function sumDriftCost(turns: DriftTurn[]): number {
    return turns.reduce((sum, turn) => sum + (turn.isDrift ? turn.costUsd : 0), 0);
}
